import { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import { StringSchema } from 'yup';

import { getPasswordLength } from '@/shared/lib/password';

import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PERSON_NAME_REGEX,
  PHONE_NUMBER_REGEX,
} from './constants';
import { PasswordValidationContext } from './types';

const isEmptyValue = (value?: unknown): boolean =>
  value === undefined || value === null || value === '';

export const withPersonNameValidation = <
  T extends StringSchema<string | undefined, object, undefined>,
>(
  schema: T,
) =>
  schema.test(
    'person-name',
    (): ReactElement => (
      <FormattedMessage
        id="validation.name.invalid"
        defaultMessage="Please enter letters only; spaces and hyphens are allowed."
      />
    ),
    (value) => {
      if (isEmptyValue(value)) {
        return true;
      }

      if (typeof value !== 'string') {
        return false;
      }

      if (value.trim() !== value) {
        return false;
      }

      return PERSON_NAME_REGEX.test(value);
    },
  ) as T;

export const withPhoneNumberValidation = <
  T extends StringSchema<string | undefined, object, undefined>,
>(
  schema: T,
) =>
  schema.test(
    'phone-number',
    (): ReactElement => (
      <FormattedMessage
        id="validation.phone.invalid"
        defaultMessage='Please enter digits only; a leading "+" is optional.'
      />
    ),
    (value) => {
      if (isEmptyValue(value)) {
        return true;
      }

      if (typeof value !== 'string') {
        return false;
      }

      if (value.trim() !== value) {
        return false;
      }

      return PHONE_NUMBER_REGEX.test(value);
    },
  ) as T;

export const withPasswordMaxLengthValidation = <
  T extends StringSchema<string | undefined, object, undefined>,
>(
  schema: T,
) =>
  schema.test(
    'password-max-length',
    (): ReactElement => (
      <FormattedMessage
        id="yup.max"
        defaultMessage="Must be at most {max} characters"
        values={{ max: MAX_PASSWORD_LENGTH }}
      />
    ),
    (value) => getPasswordLength(value ?? '') <= MAX_PASSWORD_LENGTH,
  ) as T;

export const withPasswordValidation = <
  T extends StringSchema<string | undefined, object, undefined>,
>(
  schema: T,
) =>
  withPasswordMaxLengthValidation(schema).test(
    'password-min-length',
    (value, { options }) =>
      getPasswordLength(value ?? '') >=
      ((options.context as PasswordValidationContext | undefined)
        ?.passwordMinLength ?? MIN_PASSWORD_LENGTH),
  ) as T;
